/**
 * A one-shot confetti burst. No dependency.
 *
 * Draws to a full-screen, pointer-events-none <canvas> pinned above
 * everything, animates a few hundred gravity-driven paper bits, then removes
 * itself. Deliberately minimal: this marks the two moments the product exists
 * to produce — the link is ready, and the place is decided — not a general
 * effects layer.
 *
 * Fires at most once per moment. Both screens poll or re-render, and confetti
 * that restarts every five seconds stops reading as celebration.
 */

/** The product's accent, plus warmer neighbours so it reads as paper. */
const COLORS = ['#1f6b45', '#6ac28f', '#e6b422', '#d9734a', '#8a5fb0', '#f4f1ec']

interface Bit {
  x: number
  y: number
  vx: number
  vy: number
  size: number
  color: string
  rot: number
  vr: number
}

/** Fire a burst from the top-centre of the viewport. */
export function fireConfetti(durationMs = 2200): void {
  if (typeof document === 'undefined') return
  // Respect reduced-motion — skip the animation entirely.
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return

  const canvas = document.createElement('canvas')
  canvas.setAttribute('aria-hidden', 'true')
  Object.assign(canvas.style, {
    position: 'fixed',
    inset: '0',
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
    zIndex: '2147483646',
  } satisfies Partial<CSSStyleDeclaration>)
  document.body.appendChild(canvas)

  const ctx = canvas.getContext('2d')
  if (!ctx) {
    canvas.remove()
    return
  }

  const dpr = Math.min(window.devicePixelRatio || 1, 2)
  const resize = () => {
    canvas.width = window.innerWidth * dpr
    canvas.height = window.innerHeight * dpr
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  }
  resize()

  const W = window.innerWidth
  const count = Math.min(220, Math.round(W / 3))
  const originX = W / 2
  const originY = window.innerHeight * 0.18
  const bits: Bit[] = Array.from({ length: count }, () => {
    const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 1.1
    const speed = 6 + Math.random() * 8
    return {
      x: originX + (Math.random() - 0.5) * 60,
      y: originY,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed - 2,
      size: 5 + Math.random() * 6,
      color: COLORS[(Math.random() * COLORS.length) | 0]!,
      rot: Math.random() * Math.PI,
      vr: (Math.random() - 0.5) * 0.3,
    }
  })

  const gravity = 0.22
  const drag = 0.995
  const start = performance.now()
  let raf = 0

  const frame = (now: number) => {
    const elapsed = now - start
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    // Fade out over the last 600ms so bits don't vanish abruptly.
    const fade = Math.max(0, Math.min(1, (durationMs - elapsed) / 600))
    for (const b of bits) {
      b.vx *= drag
      b.vy = b.vy * drag + gravity
      b.x += b.vx
      b.y += b.vy
      b.rot += b.vr
      ctx.save()
      ctx.globalAlpha = fade
      ctx.translate(b.x, b.y)
      ctx.rotate(b.rot)
      ctx.fillStyle = b.color
      ctx.fillRect(-b.size / 2, -b.size / 2, b.size, b.size * 0.6)
      ctx.restore()
    }
    if (elapsed < durationMs) {
      raf = requestAnimationFrame(frame)
    } else {
      window.removeEventListener('resize', resize)
      canvas.remove()
    }
  }

  window.addEventListener('resize', resize)
  raf = requestAnimationFrame(frame)

  // Safety cleanup in case the tab is backgrounded mid-run and rAF stalls.
  window.setTimeout(() => {
    if (canvas.isConnected) {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', resize)
      canvas.remove()
    }
  }, durationMs + 500)
}
